import * as Sentry from '@sentry/react-native'
import { playUiSound } from '../../../../utils/play-ui-sound.util'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dimensions,
  LayoutChangeEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native'
import { IconButton, Text } from 'react-native-paper'
import { LinearGradient } from 'expo-linear-gradient'
import Svg, { Path } from 'react-native-svg'
import Animated, {
  Easing,
  interpolateColor,
  useAnimatedProps,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { AppTheme, useTheme } from '../../../../hooks/theme.hook'
import { ReplaySlider } from '../../../core/replay/components/replay.slider'
import {
  AXIS_LABEL_HEIGHT,
  computeAxisBounds,
  corankAtSeconds,
  DEFAULT_ANIMATION_DURATION_SEC,
  DEFAULT_LABEL_STEP,
  DEFAULT_PIXELS_PER_CORANK,
  DIRECTIONS_RUN_PATH,
  HIGHLIGHT_COLOR,
  HIGHLIGHT_COLOR_ALT,
  HORIZONTAL_GUTTER,
  INNER_SHADOW_HEIGHT,
  resolveRunnerTimeline,
  RUNNER_COLUMN_WIDTH,
  RUNNER_ICON_SIZE,
  totalSliderStepsForDuration,
  TRACK_HEIGHT,
  corankRunnersReplayFingerprint,
  type CorankTrackerCardParams,
  type CorankTrackerRunner,
} from './corank-tracker.model'
import { useCorankUserDifferenceQuery } from '../../../../generated/graphql'

export type {
  CorankTimelinePoint,
  CorankTrackerCardParams,
  CorankTrackerRunner,
} from './corank-tracker.model'

export {
  corankAtSeconds,
  interpolateCorankSlabSamplesToReplayKeyframes,
  interpolateCorankTimelineForReplay,
  resolveRunnerTimeline,
} from './corank-tracker.model'

function createCorankTrackerCardStyles(theme: AppTheme) {
  return StyleSheet.create({
    card: {
      borderRadius: theme.roundness * 4,
      backgroundColor: theme.colors.surface,
      overflow: 'hidden',
      paddingBottom: theme.spacing(2),
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
      elevation: 2,
    },
    innerShadowTop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: INNER_SHADOW_HEIGHT,
      zIndex: 2,
    },
    headerRow: {
      width: '100%',
      flexDirection: 'row',
      paddingHorizontal: theme.spacing(4),
      paddingVertical: theme.spacing(2),
      alignItems: 'center',
    },
    headerTitleSlot: {
      width: '50%',
      maxWidth: '50%',
      minWidth: 0,
      paddingRight: theme.spacing(2),
      justifyContent: 'center',
    },
    trackTitle: {
      color: theme.colors.onSurface,
    },
    headerLastUpdateSlot: {
      width: '50%',
      maxWidth: '50%',
      minWidth: 0,
      alignItems: 'flex-end',
      justifyContent: 'center',
    },
    lastUpdateLine: {
      width: '100%',
      textAlign: 'right',
    },
    scrollContent: {
      paddingBottom: theme.spacing(1),
    },
    track: {
      height: TRACK_HEIGHT + AXIS_LABEL_HEIGHT,
      position: 'relative',
    },
    emptyOverlay: {
      ...StyleSheet.absoluteFillObject,
      bottom: AXIS_LABEL_HEIGHT,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: theme.spacing(4),
      zIndex: 1,
    },
    emptyText: {
      textAlign: 'center',
    },
    runnerLayer: {
      ...StyleSheet.absoluteFillObject,
      bottom: AXIS_LABEL_HEIGHT,
    },
    runner: {
      position: 'absolute',
      bottom: 0,
      width: RUNNER_COLUMN_WIDTH,
      alignItems: 'center',
    },
    runnerElevated: {
      zIndex: 3,
    },
    runnerName: {
      maxWidth: RUNNER_COLUMN_WIDTH,
      textAlign: 'center',
      marginBottom: 2,
    },
    runnerCorank: {
      marginTop: 2,
    },
    runnerPressablePressed: {
      opacity: 0.85,
    },
    gridLayer: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: AXIS_LABEL_HEIGHT,
      height: TRACK_HEIGHT,
    },
    gridLine: {
      position: 'absolute',
      bottom: 0,
      width: StyleSheet.hairlineWidth,
      backgroundColor: theme.colors.outlineVariant,
    },
    gridLineMajor: {
      height: TRACK_HEIGHT,
      opacity: 0.9,
    },
    gridLineMinor: {
      height: TRACK_HEIGHT * 0.45,
      opacity: 0.5,
    },
    baseline: {
      position: 'absolute',
      bottom: AXIS_LABEL_HEIGHT - 1,
      height: StyleSheet.hairlineWidth,
      backgroundColor: theme.colors.outline,
    },
    labelsRow: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: AXIS_LABEL_HEIGHT,
    },
    axisLabel: {
      position: 'absolute',
      width: 24,
      textAlign: 'center',
      color: theme.colors.onSurfaceVariant,
    },
    playbackRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: theme.spacing(2),
      marginTop: theme.spacing(1),
    },
    sliderWrap: {
      flex: 1,
      minHeight: 36,
      justifyContent: 'center',
    },
  })
}

const AnimatedPath = Animated.createAnimatedComponent(Path)

function useCorankTrackPlayback(params: { durationSec: number }) {
  const { durationSec } = params
  const durationSeconds = Math.max(1, durationSec)

  const totalSliderSteps = useMemo(
    () => totalSliderStepsForDuration(durationSeconds),
    [durationSeconds]
  )

  const [playbackTimeSeconds, setPlaybackTimeSeconds] =
    useState(durationSeconds)
  const [isPlaying, setIsPlaying] = useState(false)
  const playingRef = useRef(false)
  playingRef.current = isPlaying

  const rafRef = useRef<number | null>(null)
  const lastTickRef = useRef<number>(0)
  const durationInitRef = useRef(false)

  useEffect(() => {
    if (!durationInitRef.current) {
      durationInitRef.current = true
      return
    }
    setPlaybackTimeSeconds(durationSeconds)
    setIsPlaying(false)
  }, [durationSeconds])

  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      return
    }
    lastTickRef.current = performance.now()
    const tick = (now: number) => {
      if (!playingRef.current) {
        return
      }
      const deltaSeconds = (now - lastTickRef.current) / 1000
      lastTickRef.current = now
      setPlaybackTimeSeconds((previousSeconds) => {
        const nextSeconds = previousSeconds + deltaSeconds
        if (nextSeconds >= durationSeconds) {
          playingRef.current = false
          queueMicrotask(() => setIsPlaying(false))
          return durationSeconds
        }
        return nextSeconds
      })
      if (playingRef.current) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [isPlaying, durationSeconds])

  const sliderProgress = Math.round(
    (playbackTimeSeconds / durationSeconds) * totalSliderSteps
  )

  const onSeek = useCallback(
    (step: number) => {
      const clampedStep = Math.max(0, Math.min(step, totalSliderSteps))
      setPlaybackTimeSeconds((clampedStep / totalSliderSteps) * durationSeconds)
      setIsPlaying(false)
    },
    [totalSliderSteps, durationSeconds]
  )

  const replayFromStart = useCallback(() => {
    void playUiSound('race-start')
    setPlaybackTimeSeconds(0)
    setIsPlaying(true)
  }, [])

  const onPlay = useCallback(() => {
    const atEnd = playbackTimeSeconds >= durationSeconds - 0.02
    const atStart = playbackTimeSeconds <= 0.02
    if (atEnd || atStart) {
      void playUiSound('race-start')
    }
    if (atEnd) {
      setPlaybackTimeSeconds(0)
    }
    setIsPlaying(true)
  }, [playbackTimeSeconds, durationSeconds])

  const onPause = useCallback(() => {
    setIsPlaying(false)
  }, [])

  const onReplay = useCallback(() => {
    replayFromStart()
  }, [replayFromStart])

  return {
    playbackTimeSeconds,
    isPlaying,
    totalSliderSteps,
    sliderProgress,
    onSeek,
    onPlay,
    onPause,
    onReplay,
    replayFromStart,
  }
}

function DirectionsRunIcon({ size, color }: { size: number; color: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 55 55">
      <Path d={DIRECTIONS_RUN_PATH} fill={color} />
    </Svg>
  )
}

function DirectionsRunIconHighlighted({ size }: { size: number }) {
  const progress = useSharedValue(0)

  useEffect(() => {
    progress.value = withRepeat(
      withTiming(1, { duration: 1800, easing: Easing.inOut(Easing.sin) }),
      -1,
      true
    )
  }, [])

  const animatedProps = useAnimatedProps(() => ({
    fill: interpolateColor(
      progress.value,
      [0, 1],
      [HIGHLIGHT_COLOR, HIGHLIGHT_COLOR_ALT]
    ),
  }))

  return (
    <Svg width={size} height={size} viewBox="0 0 55 55">
      <AnimatedPath d={DIRECTIONS_RUN_PATH} animatedProps={animatedProps} />
    </Svg>
  )
}

function CorankLastUpdateDifferenceLine({
  runners,
}: {
  runners: CorankTrackerRunner[]
}) {
  const { t } = useTranslation('profile')
  const theme = useTheme()
  const styles = useMemo(() => createCorankTrackerCardStyles(theme), [theme])
  const profileRunner = useMemo(
    () => runners.find((runner) => !runner.isHighlighted),
    [runners]
  )
  const viewerRunner = useMemo(
    () => runners.find((runner) => runner.isHighlighted),
    [runners]
  )
  const profileUserIdForDifference = profileRunner?.userId
  const comparisonNodeIdForDifference =
    profileRunner?.corankScopeNodeId ?? viewerRunner?.corankScopeNodeId
  const shouldFetchLastUpdateDifference = Boolean(
    profileUserIdForDifference &&
      viewerRunner?.userId &&
      profileUserIdForDifference !== viewerRunner.userId
  )
  const {
    data: lastUpdateData,
    loading: lastUpdateLoading,
    error: lastUpdateError,
  } = useCorankUserDifferenceQuery({
    variables: {
      profileUserId: profileUserIdForDifference ?? '',
      nodeId:
        comparisonNodeIdForDifference != null &&
        comparisonNodeIdForDifference !== ''
          ? comparisonNodeIdForDifference
          : undefined,
    },
    skip: !shouldFetchLastUpdateDifference,
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (!lastUpdateError) {
      return
    }
    console.error('corankUserDifference query failed', lastUpdateError)
    Sentry.captureException(lastUpdateError)
  }, [lastUpdateError])

  if (!shouldFetchLastUpdateDifference || !profileRunner || !viewerRunner) {
    return null
  }

  return (
    <View style={styles.headerLastUpdateSlot}>
      <Text
        variant="bodySmall"
        numberOfLines={2}
        style={[
          styles.lastUpdateLine,
          { color: theme.colors.onSurfaceVariant },
        ]}
        accessibilityRole="text"
      >
        {lastUpdateLoading
          ? t('corank-last-update-loading')
          : t('corank-last-update-line', {
              viewer:
                viewerRunner.userName?.trim() || t('corank-last-update-you'),
              viewerScore:
                lastUpdateData?.corankUserDifference.viewerLastUpdateScore ??
                '—',
              profile:
                profileRunner.userName?.trim() || t('corank-last-update-peer'),
              profileScore:
                lastUpdateData?.corankUserDifference
                  .profileUserLastUpdateScore ?? '—',
            })}
      </Text>
    </View>
  )
}

export function CorankTrackerCard(params: CorankTrackerCardParams) {
  const {
    runners,
    showEmptyTrack = false,
    corankMin,
    corankMax,
    labelStep = DEFAULT_LABEL_STEP,
    pixelsPerCorank = DEFAULT_PIXELS_PER_CORANK,
    animationDurationSec = DEFAULT_ANIMATION_DURATION_SEC,
    showPlaybackBar = true,
    onRunnerProfilePress,
    followHighlightedRunner = true,
  } = params
  const { t } = useTranslation('profile')
  const theme = useTheme()
  const scrollRef = useRef<ScrollView>(null)
  const [viewportWidth, setViewportWidth] = useState(0)
  const styles = useMemo(() => createCorankTrackerCardStyles(theme), [theme])

  const durationSeconds = Math.max(1, animationDurationSec)
  const {
    playbackTimeSeconds,
    isPlaying,
    totalSliderSteps,
    sliderProgress,
    onSeek,
    onPlay,
    onPause,
    onReplay,
    replayFromStart,
  } = useCorankTrackPlayback({ durationSec: durationSeconds })

  const isEmpty = runners.length === 0

  const replayFingerprint = useMemo(
    () => corankRunnersReplayFingerprint(runners),
    [runners]
  )
  const lastReplayFingerprintRef = useRef<string | undefined>(undefined)
  const didAutoPlayInitialRaceRef = useRef(false)
  useEffect(() => {
    if (isEmpty) {
      return
    }
    if (didAutoPlayInitialRaceRef.current) {
      return
    }
    didAutoPlayInitialRaceRef.current = true
    replayFromStart()
  }, [isEmpty, replayFromStart])

  useEffect(() => {
    if (isEmpty) {
      return
    }
    if (lastReplayFingerprintRef.current === undefined) {
      lastReplayFingerprintRef.current = replayFingerprint
      return
    }
    if (lastReplayFingerprintRef.current === replayFingerprint) {
      return
    }
    lastReplayFingerprintRef.current = replayFingerprint
    replayFromStart()
  }, [isEmpty, replayFingerprint, replayFromStart])

  const { min, max } = useMemo(
    () => computeAxisBounds(runners, corankMin, corankMax),
    [runners, corankMin, corankMax]
  )

  const resolvedTimelines = useMemo(
    () =>
      runners.map((runner) => ({
        runner,
        points: resolveRunnerTimeline(runner, durationSeconds),
      })),
    [runners, durationSeconds]
  )

  const displayCorankByUserName = useMemo(() => {
    const map = new Map<string, number>()
    resolvedTimelines.forEach(({ runner, points }) => {
      map.set(runner.userName, corankAtSeconds(points, playbackTimeSeconds))
    })
    return map
  }, [resolvedTimelines, playbackTimeSeconds])

  const corankSpan = Math.max(1, max - min)

  const { effectivePixelsPerCorank, contentWidth } = useMemo(() => {
    const naturalWidth = HORIZONTAL_GUTTER * 2 + corankSpan * pixelsPerCorank
    if (viewportWidth <= 0) {
      return {
        effectivePixelsPerCorank: pixelsPerCorank,
        contentWidth: naturalWidth,
      }
    }
    const innerWidth = viewportWidth - HORIZONTAL_GUTTER * 2
    const minPixelsPerCorankToFillViewport = innerWidth / corankSpan
    const effectivePixelsPerCorank = Math.max(
      pixelsPerCorank,
      minPixelsPerCorankToFillViewport
    )
    return {
      effectivePixelsPerCorank,
      contentWidth:
        HORIZONTAL_GUTTER * 2 + corankSpan * effectivePixelsPerCorank,
    }
  }, [viewportWidth, corankSpan, pixelsPerCorank])

  const onScrollViewportLayout = (event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width
    if (width > 0) {
      setViewportWidth(width)
    }
  }

  const tickIndices = useMemo(
    () =>
      Array.from(
        { length: Math.max(0, max - min + 1) },
        (_, index) => min + index
      ),
    [min, max]
  )

  const labelTicks = useMemo(() => {
    const firstLabel = Math.ceil(min / labelStep) * labelStep
    if (firstLabel > max) {
      return []
    }
    const tickCount = Math.floor((max - firstLabel) / labelStep) + 1
    return Array.from({ length: tickCount }, (_, index) => {
      return firstLabel + index * labelStep
    })
  }, [min, max, labelStep])

  const screenWidth = Dimensions.get('window').width

  const scrollTrackToCenterCorank = useCallback(
    (corank: number, animated: boolean) => {
      const visibleWidth = viewportWidth > 0 ? viewportWidth : screenWidth
      if (visibleWidth <= 0) {
        return
      }
      const centerX =
        HORIZONTAL_GUTTER + (corank - min) * effectivePixelsPerCorank
      const targetScrollX = Math.max(
        0,
        centerX - visibleWidth / 2 + RUNNER_COLUMN_WIDTH / 2
      )
      const maxScroll = Math.max(0, contentWidth - visibleWidth)
      const scrollX = Math.min(targetScrollX, maxScroll)
      scrollRef.current?.scrollTo({ x: scrollX, animated })
    },
    [min, effectivePixelsPerCorank, contentWidth, screenWidth, viewportWidth]
  )

  /** When paused: center on the highlighted user’s final co-rank (initial layout, end of replay). */
  useEffect(() => {
    if (!followHighlightedRunner) {
      return
    }
    if (isPlaying) {
      return
    }
    const highlightedRunner = runners.find((runner) => runner.isHighlighted)
    if (!highlightedRunner) {
      return
    }
    scrollTrackToCenterCorank(highlightedRunner.corank, true)
  }, [followHighlightedRunner, isPlaying, runners, scrollTrackToCenterCorank])

  /** While playing: keep the highlighted (main) user centered using the animated co-rank. */
  useEffect(() => {
    if (!followHighlightedRunner) {
      return
    }
    if (!isPlaying) {
      return
    }
    const highlightedRunner = runners.find((runner) => runner.isHighlighted)
    if (!highlightedRunner) {
      return
    }
    const displayCorank =
      displayCorankByUserName.get(highlightedRunner.userName) ??
      highlightedRunner.corank
    scrollTrackToCenterCorank(displayCorank, false)
  }, [
    followHighlightedRunner,
    isPlaying,
    playbackTimeSeconds,
    runners,
    displayCorankByUserName,
    scrollTrackToCenterCorank,
  ])

  const sortedRunners = [...runners].sort((leftRunner, rightRunner) => {
    if (leftRunner.isHighlighted) {
      return 1
    }
    if (rightRunner.isHighlighted) {
      return -1
    }
    return leftRunner.corank - rightRunner.corank
  })

  if (isEmpty && !showEmptyTrack) {
    return null
  }

  const showPlaybackRow = showPlaybackBar && !isEmpty

  return (
    <View style={[styles.card]} accessibilityRole="summary">
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(0,0,0,0.14)', 'rgba(0,0,0,0.06)', 'transparent']}
        locations={[0, 0.35, 1]}
        style={styles.innerShadowTop}
      />
      <View style={styles.headerRow}>
        <View style={styles.headerTitleSlot}>
          <Text variant="titleSmall" style={styles.trackTitle}>
            {t('corank-track-title')}
          </Text>
        </View>
        <CorankLastUpdateDifferenceLine runners={runners} />
      </View>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        onLayout={onScrollViewportLayout}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.track, { width: contentWidth }]}>
          {isEmpty ? (
            <View style={styles.emptyOverlay} pointerEvents="none">
              <Text
                variant="bodySmall"
                style={[
                  styles.emptyText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                {t('corank-track-empty')}
              </Text>
            </View>
          ) : null}

          <View style={styles.runnerLayer}>
            {sortedRunners.map((runner) => {
              const displayCorank =
                displayCorankByUserName.get(runner.userName) ?? runner.corank
              const runnerLeft =
                HORIZONTAL_GUTTER +
                (displayCorank - min) * effectivePixelsPerCorank -
                RUNNER_COLUMN_WIDTH / 2
              const mutedColor = theme.colors.onSurfaceDisabled
              const primaryColor = HIGHLIGHT_COLOR
              const runnerColor = runner.isHighlighted
                ? primaryColor
                : mutedColor
              const canOpenProfile =
                Boolean(onRunnerProfilePress) && Boolean(runner.userId)
              const runnerLayoutStyle = [
                styles.runner,
                { left: runnerLeft },
                runner.isHighlighted ? styles.runnerElevated : undefined,
              ]
              const runnerContent = (
                <>
                  <Text
                    variant="labelSmall"
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={[styles.runnerName, { color: runnerColor }]}
                  >
                    {runner.userName}
                  </Text>
                  {runner.isHighlighted ? (
                    <DirectionsRunIconHighlighted size={RUNNER_ICON_SIZE} />
                  ) : (
                    <DirectionsRunIcon
                      size={RUNNER_ICON_SIZE}
                      color={runnerColor}
                    />
                  )}
                  {!isPlaying ? (
                    <Text
                      variant="labelMedium"
                      style={[styles.runnerCorank, { color: runnerColor }]}
                    >
                      {Math.round(displayCorank)}
                    </Text>
                  ) : null}
                </>
              )
              if (canOpenProfile) {
                return (
                  <Pressable
                    key={runner.userName}
                    style={({ pressed }) => [
                      ...runnerLayoutStyle,
                      pressed ? styles.runnerPressablePressed : undefined,
                    ]}
                    onPress={() => {
                      if (!onRunnerProfilePress || !runner.userId) {
                        return
                      }
                      onRunnerProfilePress({ userId: runner.userId })
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={t('corank-track-runner-profile-a11y', {
                      name: runner.userName,
                    })}
                  >
                    {runnerContent}
                  </Pressable>
                )
              }
              return (
                <View key={runner.userName} style={runnerLayoutStyle}>
                  {runnerContent}
                </View>
              )
            })}
          </View>

          <View style={styles.gridLayer} pointerEvents="none">
            {tickIndices.map((corankTick) => {
              const isMajorTick = corankTick % labelStep === 0
              const gridLineLeft =
                HORIZONTAL_GUTTER +
                (corankTick - min) * effectivePixelsPerCorank -
                0.5
              return (
                <View
                  key={`tick-${corankTick}`}
                  style={[
                    styles.gridLine,
                    isMajorTick ? styles.gridLineMajor : styles.gridLineMinor,
                    { left: gridLineLeft },
                  ]}
                />
              )
            })}
          </View>

          <View
            style={[
              styles.baseline,
              {
                left: HORIZONTAL_GUTTER,
                width: contentWidth - HORIZONTAL_GUTTER * 2,
              },
            ]}
          />

          <View style={styles.labelsRow}>
            {labelTicks.map((axisLabelValue) => {
              const axisLabelLeft =
                HORIZONTAL_GUTTER +
                (axisLabelValue - min) * effectivePixelsPerCorank -
                10
              return (
                <Text
                  key={`label-${axisLabelValue}`}
                  variant="labelSmall"
                  style={[styles.axisLabel, { left: axisLabelLeft }]}
                >
                  {axisLabelValue}
                </Text>
              )
            })}
          </View>
        </View>
      </ScrollView>

      {showPlaybackRow ? (
        <View style={styles.playbackRow}>
          <IconButton
            icon={isPlaying ? 'pause' : 'play'}
            size={22}
            onPress={isPlaying ? onPause : onPlay}
            accessibilityLabel={
              isPlaying
                ? t('corank-track-pause-a11y')
                : t('corank-track-play-a11y')
            }
          />
          <View style={styles.sliderWrap}>
            <ReplaySlider
              progress={sliderProgress}
              onSeek={onSeek}
              totalSteps={totalSliderSteps}
            />
          </View>
          <IconButton
            icon="replay"
            size={22}
            onPress={onReplay}
            accessibilityLabel={t('corank-track-restart-a11y')}
          />
        </View>
      ) : null}
    </View>
  )
}
